import type { SettingsState } from '../contexts/SettingsContext';
import { isApiError } from '../utils/apiErrors';
import type { StoredEvent } from './EventsService';
import { apiGet, apiPut, apiPatch, apiDelete , planningPath } from './PlanningApiClient';

export interface OnboardingProfileData {
  hashtag: string;
  instagram: string;
  guestCapacity: string;
  budgetCeiling: string;
  city: string;
  sheetsId: string;
}

export interface UserProfile {
  owner_email: string;
  createdAt: string;
  updatedAt: string;
  onboardingCompleted: boolean;
  settings: Partial<SettingsState>;
  events: StoredEvent[];
  onboarding: Partial<OnboardingProfileData>;
}

export interface UserProfilePayload {
  onboardingCompleted: boolean;
  settings: Partial<SettingsState>;
  events: StoredEvent[];
  onboarding: Partial<OnboardingProfileData>;
}

export interface UserProfilePatch {
  onboardingCompleted?: boolean;
  settings?: Partial<SettingsState>;
  events?: StoredEvent[];
  onboarding?: Partial<OnboardingProfileData>;
}

let _cachedEmail: string | null = null;

/** Set the current user email for profile operations. Call from AuthContext after login. */
export function setProfileUserEmail(email: string): void {
  _cachedEmail = email;
}

function getEmail(): string {
  if (!_cachedEmail) {
    throw new Error('User email not set. Call setProfileUserEmail() after login.');
  }
  return _cachedEmail;
}

export async function getUserProfile(): Promise<UserProfile | null> {
  try {
    return await apiGet<UserProfile>(
      planningPath(`/user-profile?email=${encodeURIComponent(getEmail())}`)
    );
  } catch (error) {
    if (isApiError(error) && error.status === 404) {
      return null;
    }
    throw error;
  }
}

export function createOrReplaceUserProfile(payload: UserProfilePayload): Promise<UserProfile> {
  return apiPut<UserProfile>(planningPath('/user-profile'), {
    owner_email: getEmail(),
    ...payload,
  });
}

export function patchUserProfile(payload: UserProfilePatch): Promise<UserProfile> {
  return apiPatch<UserProfile>(planningPath('/user-profile'), {
    owner_email: getEmail(),
    ...payload,
  });
}

export function deleteUserProfile(): Promise<void> {
  return apiDelete(planningPath(`/user-profile?email=${encodeURIComponent(getEmail())}`));
}
